/**
 * TRBUD-447: the records behind one figure of a saved, pinned or builder report.
 *
 * A drill names the report (its definition, never SQL), the day the figure was
 * made (`asOf`, the figure's own `window.to`), the cell that was clicked and
 * which of its measures. The server compiles the SAME definition the figure came
 * from, filters its row set to the cell and lists every row (or recorded line)
 * with the exact amount it adds to the figure. The list total and the figure are
 * both worked out in one snapshot, so a list that disagrees with its figure is a
 * change in the data, never a different rule.
 *
 * Browser-safe: zod, types and the registry (pure data).
 */
import { z } from "zod";
import { calendarDate, strictObject } from "./requests.js";
import {
  matrixDefinitionBody, type MatrixBaseId, type MatrixDefinition, type MatrixField, type MatrixFormat,
  type MatrixScalar, type MatrixType,
} from "./report-matrix.js";
import { REPORT_MATRIX_BASES } from "./report-matrix-registry.js";

/** The most rows one page of a drill returns. */
export const MATRIX_DRILL_PAGE = 100;
/** The most rows a drill export writes; the file says when it stopped. */
export const MATRIX_DRILL_EXPORT_CAP = 100000;

const scalar = z.union([z.string().max(500), z.number().finite(), z.boolean(), z.null()]);
/** A chart axis value as the chart returned it: its text, or the Other bucket. */
const axisValue = strictObject({ value: z.union([z.string().max(500), z.null()]), other: z.boolean() });

/**
 * The clicked cell.
 * - `row`: a matrix row, named by every grouped column's value (`c0`, `c2`...).
 * - `total`: the Grand total row, or a KPI tile (both are the report's totals).
 * - `counted_once`: the de-duplicated total of a report grouped by tags.
 * - `chart`: a bar or point, by its X (and series) value or the Other bucket.
 * - `detail_row`: one row of a detail report, by its hidden row key.
 */
export const matrixDrillCell = z.discriminatedUnion("kind", [
  strictObject({ kind: z.literal("row"), keys: z.record(z.string().regex(/^c\d{1,2}$/), scalar) }),
  strictObject({ kind: z.literal("total") }),
  strictObject({ kind: z.literal("counted_once") }),
  strictObject({ kind: z.literal("chart"), x: axisValue, series: axisValue.optional() }),
  strictObject({ kind: z.literal("detail_row"), rowKey: z.string().min(1).max(200) }),
]);
export type MatrixDrillCell = z.infer<typeof matrixDrillCell>;
export type MatrixDrillSide = "value" | "numerator" | "denominator";
export type MatrixDrillLevel = "rows" | "lines";

const drillShape = {
  definition: matrixDefinitionBody,
  /** The figure's window end. Today for a fresh figure; never a future day. */
  asOf: calendarDate,
  cell: matrixDrillCell,
  /** The index of the clicked measure in `definition.columns`. */
  column: z.number().int().min(0).max(29),
  /** A ratio lists its top (numerator) or bottom (denominator); anything else its value. */
  side: z.enum(["value", "numerator", "denominator"]).default("value"),
  /** Recorded lines where the figure is a total of them, else the rows. */
  level: z.enum(["rows", "lines"]).default("lines"),
};
export const matrixDrillBody = strictObject({
  ...drillShape,
  offset: z.number().int().min(0).max(MATRIX_DRILL_EXPORT_CAP).default(0),
  limit: z.number().int().min(1).max(MATRIX_DRILL_PAGE).default(MATRIX_DRILL_PAGE),
});
export type MatrixDrillRequest = z.infer<typeof matrixDrillBody>;
export const exportMatrixDrillBody = strictObject({ ...drillShape, definitionId: z.uuid().optional() });
export type MatrixDrillExportRequest = z.infer<typeof exportMatrixDrillBody>;

/** A listed column. `link` columns carry the target id in `<key>__link`, and `extraLabel` columns a second text in `<key>__extra`. */
export interface MatrixDrillColumn {
  key: string; label: string; type: MatrixType; format?: MatrixFormat;
  link?: "work_order" | "asset"; extraLabel?: string;
}
export interface MatrixDrillResponse {
  /** The clicked cell, recomputed now (for a ratio, the ratio). */
  figure: MatrixScalar;
  /** What the list adds up to: the figure, or a ratio's top or bottom. */
  sideFigure: MatrixScalar;
  /** The sum of every listed row's contribution (an average's listed average). */
  listTotal: MatrixScalar;
  count: number; offset: number; hasMore: boolean;
  level: MatrixDrillLevel; side: MatrixDrillSide;
  /** Whether this figure can list recorded lines, and if not, why. */
  linesAvailable: boolean; linesNote: string | null;
  columns: MatrixDrillColumn[]; rows: Record<string, MatrixScalar>[];
  window: { from: string | null; to: string };
  /** Still-open downtime keeps counting after the figure was made. */
  openDowntime: boolean;
  notes: string[];
  currency: string | null;
  /** Plural noun for the listed rows: "jobs", "assets", "lines", "values". */
  noun: string;
}

const NOUNS: Record<MatrixBaseId, [string, string]> = {
  assets: ["asset", "assets"], work_orders: ["job", "jobs"], vendors: ["vendor", "vendors"], parts: ["part", "parts"],
  vendor_invoices: ["invoice", "invoices"], money_log: ["cost line", "cost lines"], time_log: ["time entry", "time entries"],
  downtime_log: ["downtime piece", "downtime pieces"], parts_log: ["movement", "movements"],
  po_variance: ["invoice line", "invoice lines"], stage_history: ["stage visit", "stage visits"],
};
/** "job" / "jobs" for a work-order report, and so on. */
export function matrixDrillNoun(base: MatrixBaseId, plural = true): string {
  return NOUNS[base][plural ? 1 : 0];
}

/** What a column of a report offers when clicked. */
export interface MatrixDrillTarget {
  /** Body cells (a grouped row's measure, or a detail row's flow total). */
  rows: boolean;
  /** Grand total, Counted once and KPI tiles. */
  totals: boolean;
  /** The figure is a total of recorded lines (on at least one side, for a ratio). */
  lines: boolean;
  ratio: boolean;
}

function fieldOf(definition: MatrixDefinition, index: number): MatrixField | undefined {
  const base = REPORT_MATRIX_BASES.find((candidate) => candidate.id === definition.base);
  const id = definition.columns[index]?.field;
  return base?.fields.find((field) => field.id === id);
}
function flowField(definition: MatrixDefinition, id: string): boolean {
  const base = REPORT_MATRIX_BASES.find((candidate) => candidate.id === definition.base);
  return !!base?.fields.find((field) => field.id === id)?.flow;
}

/**
 * Which cells can be clicked, per column. One rule for the screen and the
 * server: a grouped report's every measure (rows, totals, Counted once, chart);
 * a detail report's summable numbers in the Grand total, and its line totals
 * (flows) on each row.
 */
export function matrixDrillTargets(definition: MatrixDefinition): (MatrixDrillTarget | null)[] {
  return definition.columns.map((column, index) => {
    const field = fieldOf(definition, index);
    if (!field) return null;
    if (definition.mode === "grouped") {
      if (!column.aggregate) return null;
      const ratio = column.aggregate === "ratio";
      const lines = ratio
        ? !!field.ratio && (flowField(definition, field.ratio.numerator) || flowField(definition, field.ratio.denominator))
        : column.aggregate === "sum" && !!field.flow;
      return { rows: true, totals: true, lines, ratio };
    }
    if (field.type !== "number" || !field.aggregates.includes("sum") || field.ratio) return null;
    return { rows: !!field.flow, totals: true, lines: !!field.flow, ratio: false };
  });
}
